import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

export interface BatteryStatus {
  charging: boolean;
  level: number;
  chargingTime: number;
  dischargingTime: number;
}

interface BatteryManager extends EventTarget {
  charging: boolean;
  level: number;
  chargingTime: number;
  dischargingTime: number;
  // Os 4 listeners do BatteryManager retornam unknown — não usamos o valor de
  // retorno, só o side-effect via setState dentro do listener.
  onchargingchange: ((this: BatteryManager, ev: Event) => unknown) | null;
  onlevelchange: ((this: BatteryManager, ev: Event) => unknown) | null;
  onchargingtimechange: ((this: BatteryManager, ev: Event) => unknown) | null;
  ondischargingtimechange:
    | ((this: BatteryManager, ev: Event) => unknown)
    | null;
}

type NavigatorWithBattery = Navigator & {
  getBattery: () => Promise<BatteryManager>;
};

export const useBatteryStatus = () => {
  const [batteryStatus, setBatteryStatus] = useState<BatteryStatus | null>(
    null,
  );
  const [isSupported, setIsSupported] = useState<boolean>(true);

  useEffect(() => {
    if (Capacitor.getPlatform() === 'android') {
      let isMounted = true;
      let pollingInterval: ReturnType<typeof setInterval> | null = null;

      const updateFromNative = async () => {
        try {
          const { Device } = await import('@capacitor/device');
          const batteryInfo = await Device.getBatteryInfo();
          if (!isMounted) return;
          if (typeof batteryInfo.batteryLevel === 'number') {
            const next = {
              charging: Boolean(batteryInfo.isCharging),
              level: batteryInfo.batteryLevel,
              chargingTime: 0,
              dischargingTime: 0,
            };
            // Diff-check (Renato 2026-07-05, gargalo A7): o poll roda a cada 5s,
            // mas o nível/charging muda raramente. Retornar `prev` (mesma ref)
            // quando nada mudou faz o React BAILAR do re-render — antes,
            // setBatteryStatus com objeto novo re-renderizava o Stream a cada 5s.
            setBatteryStatus((prev) =>
              prev &&
              prev.charging === next.charging &&
              prev.level === next.level
                ? prev
                : next,
            );
            setIsSupported(true);
          } else {
            setBatteryStatus(null);
            setIsSupported(false);
          }
        } catch (error) {
          console.warn(
            '[useBatteryStatus] Native battery not available:',
            error,
          );
          setBatteryStatus(null);
          setIsSupported(false);
        }
      };

      updateFromNative();
      pollingInterval = setInterval(updateFromNative, 5000);

      return () => {
        isMounted = false;
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      };
    }

    const isDesktopLike = () => {
      const ua = navigator.userAgent || '';
      const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
      const isDesktop = /Macintosh|MacIntel|Windows|Linux/i.test(ua);
      return isDesktop && !isMobile;
    };

    // Desabilita em laptops/desktops (não faz sentido alerta de bateria)
    if (isDesktopLike()) {
      setIsSupported(false);
      return;
    }

    const nav = navigator as NavigatorWithBattery;
    if (!nav.getBattery) {
      setIsSupported(false);
      return;
    }

    let battery: BatteryManager | null = null;
    let pollingInterval: ReturnType<typeof setInterval> | null = null;

    const updateBatteryStatus = () => {
      if (battery) {
        const bat = battery;
        // Diff-check (charging+level, os campos exibidos): bail do re-render
        // quando nada mudou (Renato 2026-07-05, gargalo A7).
        setBatteryStatus((prev) =>
          prev && prev.charging === bat.charging && prev.level === bat.level
            ? prev
            : {
                charging: bat.charging,
                level: bat.level,
                chargingTime: bat.chargingTime,
                dischargingTime: bat.dischargingTime,
              },
        );
      }
    };

    const handleBatteryChange = () => {
      updateBatteryStatus();
    };

    nav
      .getBattery()
      .then((bat) => {
        battery = bat;
        updateBatteryStatus();

        bat.addEventListener('chargingchange', handleBatteryChange);
        bat.addEventListener('levelchange', handleBatteryChange);
        bat.addEventListener('chargingtimechange', handleBatteryChange);
        bat.addEventListener('dischargingtimechange', handleBatteryChange);

        // Fallback polling in case events do not fire reliably
        pollingInterval = setInterval(updateBatteryStatus, 5000);
      })
      .catch((err) => {
        console.warn('[useBatteryStatus] Battery API not available:', err);
        setIsSupported(false);
      });

    return () => {
      if (battery) {
        battery.removeEventListener('chargingchange', handleBatteryChange);
        battery.removeEventListener('levelchange', handleBatteryChange);
        battery.removeEventListener('chargingtimechange', handleBatteryChange);
        battery.removeEventListener(
          'dischargingtimechange',
          handleBatteryChange,
        );
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, []);

  return { batteryStatus, isSupported };
};
